import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import {
  Search, Package, AlertTriangle, XCircle, CheckCircle2, Plus, Minus,
  Loader2, X, History, TrendingUp, TrendingDown, RefreshCw, Bell,
  Download, Upload, ChevronDown, ChevronRight, ScanLine, ArrowDownCircle, ArrowUpCircle,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import api from '@/lib/api';
import BulkInventoryUploadModal from './BulkInventoryUploadModal';
// BARCODE/WMS A4-1 · Inbound continuous scan modal.  Sibling of AdjustModal —
// does NOT replace 재고조정, it's an additional entry point for warehouse ops.
// A4-1C · same component drives OUTBOUND scan (delta=-1) via `mode="outbound"`.
import InboundScanModal from './InboundScanModal';
// A4-1D · shared 매입처 선택기 (재고조정 · 입고 스캔 공용).
import SupplierPicker from './SupplierPicker';
// A4-1E · 바코드 라벨 출력 (단건/다건).
import BarcodeLabelPrint from './BarcodeLabelPrint';
// A4-1G-2A · 매입처 상품정보 수정 진입점 (매입처 상세 modal REUSE).  DB source-of-truth 동일.
import { SupplierDetailModal } from './PurchaseOrderSection';

// Phase 1 Inventory Management. NO barcode scanner. NO real WMS.
// Focuses on: per-variant stock table + manual adjustment + transaction history.

const won = (n) => `${(Number(n) || 0).toLocaleString('ko-KR')}`;
const fmtDate = (d) => { if (!d) return '-'; try { return format(new Date(d), 'yyyy.MM.dd HH:mm', { locale: ko }); } catch { return '-'; } };

const STOCK_STATUS_LABEL = { normal: '정상', low_stock: '재고부족', sold_out: '품절' };
const STOCK_STATUS_CLS = {
  normal: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:border-emerald-500/30 dark:text-emerald-300',
  low_stock: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:border-amber-500/30 dark:text-amber-300',
  sold_out: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-500/10 dark:border-red-500/30 dark:text-red-300',
};

const TX_TYPE_LABEL = {
  manual_in: '수동 입고', manual_out: '수동 출고',
  order_reserved: '주문 예약', reservation_released: '예약 해제',
  shipment_confirmed: '출고 확정',
  return_restocked: '반품 재고복구', return_discarded: '반품 폐기',
  exchange_out: '교환 출고', adjustment: '재고 조정',
};

export default function InventorySection() {
  const [q, setQ] = useState('');
  // Phase 5.3B — read ?sku=... from URL and prefill search
  // A4-1B · also read ?filter=normal|low_stock|sold_out from SimpleInventory
  // card click, so the 4 main summary cards deep-link into the pre-filtered list.
  const [statusFilter, setStatusFilter] = useState('');
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const sku = params.get('sku');
      if (sku) setQ(sku);
      const f = params.get('filter');
      if (f && ['normal', 'low_stock', 'sold_out'].includes(f)) setStatusFilter(f);
    } catch (e) { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [sort, setSort] = useState('recent');
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adjustTarget, setAdjustTarget] = useState(null);
  const [txTarget, setTxTarget] = useState(null);
  const [dlOpen, setDlOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [inboundScanOpen, setInboundScanOpen] = useState(false); // A4-1
  const [outboundScanOpen, setOutboundScanOpen] = useState(false); // A4-1C
  // A4-1E · 바코드 라벨 출력 · 단건/다건.
  const [printTarget, setPrintTarget] = useState(null); // Array of rows
  const [selected, setSelected] = useState(new Set()); // multi-select "pid::vk"
  const toggleSelected = (k) => {
    const s = new Set(selected);
    if (s.has(k)) s.delete(k); else s.add(k);
    setSelected(s);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sort };
      if (q) params.q = q;
      if (statusFilter) params.stock_status = statusFilter;
      const r = await api.get('/seller/inventory', { params });
      setRows(r.data?.items || []);
      setSummary(r.data?.summary || null);
    } catch (e) { toast.error(e?.response?.data?.detail || '불러오기 실패'); }
    finally { setLoading(false); }
  }, [q, statusFilter, sort]);
  useEffect(() => { load(); }, [load]);

  const doDownload = async (kind) => {
    setDlOpen(false);
    try {
      const params = {};
      if (q) params.q = q;
      if (statusFilter) params.stock_status = statusFilter;
      const res = await api.get('/seller/inventory/export', { params });
      const rowsOut = res.data?.rows || [];
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const dataForSheet = rowsOut.map((r) => ({
        '상품ID': r.product_id, '상품명': r.product_name, '옵션': r.variant_key,
        'SKU': r.sku, '현재 총재고': r.total_stock, '예약재고': r.reserved_stock,
        '판매가능재고': r.available_stock, '재고부족 기준': r.low_stock_threshold,
        '조정수량': '', '조정사유': '',
      }));
      if (kind === 'csv') {
        const ws = XLSX.utils.json_to_sheet(dataForSheet);
        const csv = XLSX.utils.sheet_to_csv(ws);
        const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = `LIVEMOA_재고_${stamp}.csv`; a.click(); URL.revokeObjectURL(url);
      } else {
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(dataForSheet);
        XLSX.utils.book_append_sheet(wb, ws, '재고');
        XLSX.writeFile(wb, `LIVEMOA_재고_${stamp}.xlsx`);
      }
      toast.success(`${rowsOut.length}행 다운로드 완료`);
    } catch (e) { toast.error(e?.response?.data?.detail || '다운로드 실패'); }
  };

  return (
    <div className="space-y-4" data-testid="seller-inventory-section">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h2 className="text-2xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>재고관리</h2>
          <p className="text-xs text-gray-500 mt-1">상품/옵션 단위 재고 현황 · 결제완료 시 예약, 배송중 시 실제 차감.</p>
        </div>
        <button onClick={load} disabled={loading} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-black/10 dark:border-white/10 text-xs font-bold hover:bg-black/5 dark:hover:bg-white/5 disabled:opacity-40" data-testid="inv-refresh">
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> 새로고침
        </button>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="inv-toolbar">
        {/* BARCODE/WMS A4-1 · 입고 스캔 (연속 barcode → +1) · 재고조정 LOCK, 별도 진입점 */}
        <button onClick={() => setInboundScanOpen(true)}
                className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-[#FF3B30] text-white text-xs font-black hover:opacity-90"
                data-testid="inv-inbound-scan-open">
          <ArrowUpCircle size={12}/> 입고 스캔
        </button>
        {/* BARCODE/WMS A4-1C · 반출 스캔 (연속 barcode → -1) · 사유 필수 */}
        <button onClick={() => setOutboundScanOpen(true)}
                className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-amber-500 text-white text-xs font-black hover:opacity-90"
                data-testid="inv-outbound-scan-open">
          <ArrowDownCircle size={12}/> 반출 스캔
        </button>
        <button onClick={() => setBulkOpen(true)} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-black text-white dark:bg-white dark:text-black text-xs font-black hover:opacity-90" data-testid="inv-bulk-open">
          <Upload size={12}/> 재고 대량등록·수정
        </button>
        <div className="relative">
          <button onClick={() => setDlOpen((v) => !v)} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-black/10 dark:border-white/10 text-xs font-black hover:bg-black/5" data-testid="inv-download-open">
            <Download size={12}/> 재고 다운로드 <ChevronDown size={10}/>
          </button>
          {dlOpen && (
            <div className="absolute right-0 z-40 mt-1 min-w-[220px] rounded-xl border border-black/10 dark:border-white/10 bg-white dark:bg-[#141414] shadow-2xl p-1" data-testid="inv-download-menu" onMouseLeave={() => setDlOpen(false)}>
              <button onClick={() => doDownload('xlsx')} className="w-full text-left px-3 py-2 rounded text-xs hover:bg-black/5 dark:hover:bg-white/10" data-testid="inv-download-xlsx">Excel (.xlsx) · 현재 필터</button>
              <button onClick={() => doDownload('csv')} className="w-full text-left px-3 py-2 rounded text-xs hover:bg-black/5 dark:hover:bg-white/10" data-testid="inv-download-csv">CSV (UTF-8 BOM)</button>
            </div>
          )}
        </div>
        {/* A4-1E · 다건 바코드 라벨 출력 (체크박스 선택 후) */}
        {selected.size > 0 && (
          <button onClick={() => {
            const chosen = rows.filter((r) =>
              selected.has(`${r.product_id}::${r.variant_key}`));
            setPrintTarget(chosen);
          }}
                  className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border-2 border-[#FF3B30] text-[#FF3B30] text-xs font-black hover:bg-[#FF3B30]/5"
                  data-testid="inv-print-selected">
            선택 바코드 출력 ({selected.size})
          </button>
        )}
        {/* A4-1D · 매입처 관리 (재고화면 공용 진입점 · 좌측 대메뉴 추가하지 않음).
            발주 페이지의 공급처 서브탭으로 이동해 기존 관리 UI REUSE. */}
        <button onClick={() => { window.location.search = '?section=inventory&detail=purchase&subtab=suppliers'; }}
                className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border-2 border-black/20 dark:border-white/20 text-xs font-black hover:bg-black/5"
                data-testid="inv-suppliers-open">
          매입처 관리
        </button>
      </div>


      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="inv-summary-cards">
        {/* A4-1C · 4개 카드 모두 클릭 → 같은 화면 필터 (페이지 이동 없음).
            전체 옵션 카드는 제거 · 판매자가 자주 필요한 4개 상태만 노출. */}
        <SummaryCard label="정상 재고" value={summary?.normal || 0}
                     icon={CheckCircle2} tone="emerald"
                     active={statusFilter === 'normal'}
                     onClick={() => setStatusFilter(statusFilter === 'normal' ? '' : 'normal')}
                     testId="inv-sum-normal" />
        <SummaryCard label="재고 부족" value={summary?.low_stock || 0}
                     icon={AlertTriangle} tone="amber"
                     active={statusFilter === 'low_stock'}
                     onClick={() => setStatusFilter(statusFilter === 'low_stock' ? '' : 'low_stock')}
                     testId="inv-sum-low" />
        <SummaryCard label="품절" value={summary?.sold_out || 0}
                     icon={XCircle} tone="red"
                     active={statusFilter === 'sold_out'}
                     onClick={() => setStatusFilter(statusFilter === 'sold_out' ? '' : 'sold_out')}
                     testId="inv-sum-soldout" />
        <SummaryCard label="발주 필요" value={summary?.low_stock || 0}
                     icon={Package} tone="rose"
                     hint="상세 발주 관리로 이동"
                     onClick={() => { window.location.search = '?section=inventory&detail=purchase'; }}
                     testId="inv-sum-reorder" />
      </div>

      <div className="rounded-2xl border border-black/10 dark:border-white/10 p-3 space-y-3">
        <div className="flex flex-wrap gap-1" data-testid="inv-filter-tabs">
          {[{k:'',label:'전체'},{k:'normal',label:'정상'},{k:'low_stock',label:'재고부족'},{k:'sold_out',label:'품절'}].map((t) => (
            <button key={t.k || 'all'} onClick={() => setStatusFilter(t.k)} data-testid={`inv-filter-${t.k || 'all'}`}
              className={`px-3 py-1.5 rounded-full text-xs font-black border ${statusFilter === t.k ? 'bg-[#FF3B30] text-white border-[#FF3B30]' : 'border-black/10 dark:border-white/10 text-gray-600 dark:text-gray-300'}`}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <div className="flex-1 min-w-[240px] relative">
            <Search size={14} className="absolute left-3 top-3 text-gray-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()}
              placeholder="상품명 · SKU · 옵션명" className="w-full pl-8 pr-3 py-2 text-xs rounded-lg border border-black/10 dark:border-white/10 bg-transparent"
              data-testid="inv-search" />
          </div>
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="px-3 py-2 text-xs rounded-lg border border-black/10 dark:border-white/10 bg-transparent" data-testid="inv-sort">
            <option value="recent">최근 수정순</option>
            <option value="stock_asc">재고 적은순</option>
            <option value="stock_desc">재고 많은순</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center text-sm text-gray-400"><Loader2 className="animate-spin inline mr-1" size={14}/> 불러오는 중…</div>
      ) : rows.length === 0 ? (
        <div className="py-16 text-center bg-black/[0.02] dark:bg-white/[0.03] rounded-2xl border border-dashed border-black/10 dark:border-white/10" data-testid="inv-empty">
          <Package className="mx-auto text-gray-300" size={28} />
          <p className="mt-3 text-sm font-bold">해당 조건의 재고가 없습니다.</p>
        </div>
      ) : (
        <>
          {/* PC table */}
          <div className="hidden md:block overflow-x-auto rounded-2xl border border-black/10 dark:border-white/10" data-testid="inv-table">
            <table className="w-full text-xs">
              <thead className="bg-black/[0.03] dark:bg-white/5">
                <tr className="text-left">
                  <th className="px-3 py-2 font-black">상품</th>
                  <th className="px-3 py-2 font-black">옵션</th>
                  <th className="px-3 py-2 font-black">SKU</th>
                  <th className="px-3 py-2 font-black">랙 위치</th>
                  <th className="px-3 py-2 font-black text-right">총재고</th>
                  <th className="px-3 py-2 font-black text-right">예약</th>
                  <th className="px-3 py-2 font-black text-right">판매가능</th>
                  <th className="px-3 py-2 font-black text-right">입고예정</th>
                  <th className="px-3 py-2 font-black">상태</th>
                  <th className="px-3 py-2 font-black">액션</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={`${r.product_id}:${r.variant_key}`} className="border-t border-black/5 dark:border-white/10" data-testid={`inv-row-${r.product_id}-${r.variant_key}`}>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        {r.product_image && <img src={r.product_image} alt="" className="w-8 h-8 rounded object-cover bg-gray-100" />}
                        <span className="font-bold">{r.product_name || '-'}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-gray-500" data-testid={`inv-variant-${r.product_id}-${r.variant_key}`}>{r.variant_key === 'default' ? '옵션 없음' : r.variant_key}</td>
                    <td className="px-3 py-2 font-mono text-gray-500">{r.sku || '-'}</td>
                    <td className="px-3 py-2">
                      <RackLocationCell row={r} onSaved={load} />
                    </td>
                    <td className="px-3 py-2 text-right font-bold" data-testid={`inv-total-${r.product_id}-${r.variant_key}`}>{won(r.total_stock)}</td>
                    <td className="px-3 py-2 text-right text-gray-500" data-testid={`inv-reserved-${r.product_id}-${r.variant_key}`}>{won(r.reserved_stock)}</td>
                    <td className="px-3 py-2 text-right font-black text-[#FF3B30]" data-testid={`inv-available-${r.product_id}-${r.variant_key}`}>{won(r.available_stock)}</td>
                    <td className="px-3 py-2 text-right font-bold text-amber-700 dark:text-amber-400" data-testid={`inv-expected-${r.product_id}-${r.variant_key}`}>
                      {(r.expected_stock || 0) > 0 ? (
                        <>
                          {won(r.expected_stock)}
                          {r.expected_next_date && <div className="text-[10px] font-normal text-gray-500">{r.expected_next_date}</div>}
                        </>
                      ) : <span className="text-gray-300">-</span>}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-black ${STOCK_STATUS_CLS[r.stock_status]}`} data-testid={`inv-status-${r.stock_status}`}>{STOCK_STATUS_LABEL[r.stock_status]}</span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex gap-1">
                        <button onClick={() => setAdjustTarget(r)} className="px-2 py-1 rounded border border-black/10 dark:border-white/10 text-[11px] font-bold hover:bg-black/5" data-testid={`inv-adjust-${r.product_id}-${r.variant_key}`}>재고 조정</button>
                        <button onClick={() => setTxTarget(r)} className="px-2 py-1 rounded border border-black/10 dark:border-white/10 text-[11px] font-bold hover:bg-black/5" data-testid={`inv-history-${r.product_id}-${r.variant_key}`}><History size={10} className="inline"/> 이력</button>
                        {/* A4-1E · single row barcode print */}
                        <button onClick={() => setPrintTarget([r])} disabled={!r.barcode}
                          className="px-2 py-1 rounded border border-black/10 dark:border-white/10 text-[11px] font-bold hover:bg-black/5 disabled:opacity-30 disabled:cursor-not-allowed"
                          title={r.barcode ? '바코드 라벨 출력' : '바코드가 없어 출력 불가'}
                          data-testid={`inv-print-${r.product_id}-${r.variant_key}`}>
                          라벨 출력
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-2" data-testid="inv-cards">
            {rows.map((r) => (
              <div key={`${r.product_id}:${r.variant_key}:m`} className="bg-white dark:bg-[#141414] rounded-2xl border border-black/10 dark:border-white/10 p-3">
                <div className="flex items-start justify-between mb-2">
                  <div className="min-w-0">
                    <p className="font-bold text-sm truncate">{r.product_name || '-'}</p>
                    <p className="text-[11px] text-gray-500 truncate">{r.variant_key === 'default' ? '옵션 없음' : r.variant_key} · SKU {r.sku || '-'}</p>
                  </div>
                  <span className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-black ${STOCK_STATUS_CLS[r.stock_status]}`}>{STOCK_STATUS_LABEL[r.stock_status]}</span>
                </div>
                <div className="grid grid-cols-4 gap-2 text-xs text-center py-2 bg-black/[0.02] dark:bg-white/[0.03] rounded-lg">
                  <div><p className="text-[10px] text-gray-500">총재고</p><p className="font-bold">{won(r.total_stock)}</p></div>
                  <div><p className="text-[10px] text-gray-500">예약</p><p className="text-gray-500">{won(r.reserved_stock)}</p></div>
                  <div><p className="text-[10px] text-gray-500">판매가능</p><p className="font-black text-[#FF3B30]">{won(r.available_stock)}</p></div>
                  <div>
                    <p className="text-[10px] text-gray-500">입고예정</p>
                    <p className="font-bold text-amber-700 dark:text-amber-400" data-testid={`inv-m-expected-${r.product_id}-${r.variant_key}`}>{(r.expected_stock || 0) > 0 ? won(r.expected_stock) : '-'}</p>
                  </div>
                </div>
                {/* A4-1G-2F · Mobile rack location editor */}
                <div className="mt-2 flex items-center gap-2 text-[11px]">
                  <span className="font-black text-gray-500 uppercase tracking-widest">랙 위치</span>
                  <RackLocationCell row={r} onSaved={load} />
                </div>
                <div className="flex gap-2 mt-2">
                  <button onClick={() => setAdjustTarget(r)} className="flex-1 py-1.5 rounded-full border border-black/10 dark:border-white/10 text-[11px] font-bold">재고 조정</button>
                  <button onClick={() => setTxTarget(r)} className="flex-1 py-1.5 rounded-full border border-black/10 dark:border-white/10 text-[11px] font-bold"><History size={10} className="inline"/> 이력</button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {adjustTarget && <AdjustModal row={adjustTarget} onClose={() => setAdjustTarget(null)} onSaved={() => { setAdjustTarget(null); load(); }} />}
      {txTarget && <TxModal row={txTarget} onClose={() => setTxTarget(null)} />}
      <BulkInventoryUploadModal open={bulkOpen} onClose={() => setBulkOpen(false)} onApplied={load} />
      {inboundScanOpen && (
        <InboundScanModal
          mode="inbound"
          onClose={() => setInboundScanOpen(false)}
          onSaved={load}
        />
      )}
      {outboundScanOpen && (
        <InboundScanModal
          mode="outbound"
          onClose={() => setOutboundScanOpen(false)}
          onSaved={load}
        />
      )}
      {printTarget && (
        <BarcodeLabelPrint
          rows={printTarget}
          onClose={() => setPrintTarget(null)}
        />
      )}
    </div>
  );
}
// A4-1G-2F · Rack location inline editor.  Saves on blur / Enter via
//   PATCH /api/seller/inventory/rack-location. NEVER touches
//   total_stock/reserved/outbound/sku/barcode/supplier — just this string
//   field on the same seller_inventory row (variant identity preserved).
function RackLocationCell({ row, onSaved }) {
  const [val, setVal] = useState(row.rack_location || '');
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  useEffect(() => { setVal(row.rack_location || ''); setDirty(false); },
    [row.product_id, row.variant_key, row.rack_location]);
  const save = useCallback(async () => {
    const norm = (val || '').trim();
    if (norm === (row.rack_location || '').trim()) { setDirty(false); return; }
    setBusy(true);
    try {
      await api.patch('/seller/inventory/rack-location', {
        product_id: row.product_id,
        variant_key: row.variant_key,
        rack_location: norm || null,
      });
      toast.success(norm ? `랙 위치 저장 · ${norm}` : '랙 위치 해제');
      setDirty(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '랙 위치 저장 실패');
    } finally { setBusy(false); }
  }, [val, row.product_id, row.variant_key, row.rack_location, onSaved]);
  return (
    <div className="flex items-center gap-1">
      <input
        value={val}
        onChange={(e) => { setVal(e.target.value); setDirty(true); }}
        onBlur={() => { if (dirty) save(); }}
        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
        placeholder="미지정"
        disabled={busy}
        className={`w-24 px-1.5 py-1 rounded border text-[11px] font-mono ${
          dirty ? 'border-[#FF3B30] bg-[#FF3B30]/5' : 'border-black/10 dark:border-white/10 bg-transparent'
        } focus:outline-none focus:ring-1 focus:ring-[#FF3B30] disabled:opacity-40`}
        title="예: A-01-03 · 저장하려면 포커스 해제 또는 Enter"
        data-testid={`inv-rack-input-${row.product_id}-${row.variant_key}`}
      />
      {busy && <Loader2 size={11} className="animate-spin text-gray-400" />}
    </div>
  );
}



const SummaryCard = ({ label, value, icon: Icon, tone, testId, onClick, active, hint }) => {
  const toneMap = {
    emerald: 'text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/30',
    amber:   'text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-500/30',
    red:     'text-red-700 dark:text-red-400 border-red-200 dark:border-red-500/30',
    rose:    'text-rose-700 dark:text-rose-400 border-rose-200 dark:border-rose-500/30',
    gray:    'text-gray-700 dark:text-gray-300 border-black/10 dark:border-white/10',
  };
  const activeBgMap = {
    emerald: 'bg-emerald-50 dark:bg-emerald-500/10',
    amber:   'bg-amber-50 dark:bg-amber-500/10',
    red:     'bg-red-50 dark:bg-red-500/10',
    rose:    'bg-rose-50 dark:bg-rose-500/10',
    gray:    'bg-black/5 dark:bg-white/5',
  };
  const clickable = typeof onClick === 'function';
  const cls = `${toneMap[tone] || toneMap.gray} ${active ? activeBgMap[tone] : 'bg-white dark:bg-[#141414]'}`;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!clickable}
      className={`border rounded-2xl p-4 text-left w-full transition ${cls} ${clickable ? 'hover:shadow-md active:scale-[0.98] cursor-pointer' : 'cursor-default'} ${active ? 'ring-2 ring-offset-1 ring-[#FF3B30]/40' : ''}`}
      data-testid={testId}
    >
      <div className="flex items-center gap-1 text-[10px] font-black uppercase tracking-widest">
        <Icon size={12} /> {label}
        {clickable && <ChevronRight size={11} className="ml-auto opacity-40" />}
      </div>
      <p className="text-2xl font-black mt-1">{Number(value || 0).toLocaleString('ko-KR')}</p>
      {hint && <p className="text-[10px] mt-0.5 opacity-60">{hint}</p>}
    </button>
  );
};

function AdjustModal({ row, onClose, onSaved }) {
  const [delta, setDelta] = useState(0);
  const [reason, setReason] = useState('입고');
  const [memo, setMemo] = useState('');
  // A4-1D · 매입처 선택 (입고 사유일 때만 노출). transaction 에 supplier_snapshot 저장.
  const [supplierId, setSupplierId] = useState(null);
  // A4-1E · 상품의 주 매입처를 자동 조회하여 pre-fill (여러 개면 선택 유지).
  const [primaryLinks, setPrimaryLinks] = useState([]);
  const [primaryMatchedBy, setPrimaryMatchedBy] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get(
          `/seller/products/${row.product_id}/primary-supplier`,
          { params: { variant_key: row.variant_key } });
        if (!alive) return;
        const links = r.data?.links || [];
        setPrimaryLinks(links);
        setPrimaryMatchedBy(r.data?.matched_by || null);
        // A4-1F · Auto-select whenever there's a UNIQUE supplier (regardless of
        // whether it was linked at variant or default level).  Only leave
        // manual selection when multiple *distinct* suppliers are linked.
        const distinctSids = Array.from(new Set(links.map((l) => l.supplier_id)));
        if (distinctSids.length === 1 && !supplierId) {
          setSupplierId(distinctSids[0]);
        }
      } catch { /* ignore · no supplier link exists */ }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.product_id, row.variant_key]);
  // A4-1F · threshold prefill: null/undefined = "미설정" empty input.
  // NEVER coerce to 5 in state — that produced the reopen-shows-5 bug.
  const initialThresholdRaw = row.low_stock_threshold;
  const [threshold, setThreshold] = useState(
    initialThresholdRaw != null ? String(initialThresholdRaw) : '');
  useEffect(() => {
    setThreshold(row.low_stock_threshold != null ? String(row.low_stock_threshold) : '');
  }, [row.low_stock_threshold, row.product_id, row.variant_key]);
  const [busy, setBusy] = useState(false);

  const thresholdChanged = String(threshold).trim() !== (
    initialThresholdRaw != null ? String(initialThresholdRaw) : '');
  const deltaChanged = Number(delta || 0) !== 0;
  const isInbound = Number(delta || 0) > 0 || String(reason).includes('입고');
  // A4-1E/F · 자동 선택된 supplier 의 링크 (표시 정보 · 매입처 상품명/옵션/공급가).
  const activeLink = primaryLinks.find((l) => l.supplier_id === supplierId);
  const distinctSids = Array.from(new Set(primaryLinks.map((l) => l.supplier_id)));
  // A4-1G-2A · 매입처 상품정보 (매입처 상품명 / 공급가) 수정 modal state.
  //   저장 후 닫히면 primary-supplier 를 재조회해서 activeLink 표시값을 최신화.
  const [supplierDetailSid, setSupplierDetailSid] = useState(null);
  const reloadPrimary = useCallback(async () => {
    try {
      const r = await api.get(
        `/seller/products/${row.product_id}/primary-supplier`,
        { params: { variant_key: row.variant_key } });
      setPrimaryLinks(r.data?.links || []);
      setPrimaryMatchedBy(r.data?.matched_by || null);
    } catch { /* ignore */ }
  }, [row.product_id, row.variant_key]);

  const submit = async () => {
    if (!deltaChanged && !thresholdChanged) {
      toast.error('조정 수량 또는 재고부족 기준을 변경해주세요.');
      return;
    }
    setBusy(true);
    try {
      if (deltaChanged) {
        await api.post('/seller/inventory/adjust', {
          product_id: row.product_id, variant_key: row.variant_key,
          delta: Number(delta), reason, memo: memo || undefined,
          // A4-1D · supplier only meaningful for inbound (positive delta).
          ...(isInbound && supplierId ? { supplier_id: supplierId } : {}),
        });
      }
      if (thresholdChanged) {
        const t = String(threshold).trim();
        await api.post('/seller/inventory/threshold', {
          product_id: row.product_id, variant_key: row.variant_key,
          // Empty input → send 0 to clear (backend stores as 0; UI still shows
          // as "explicit" once saved).  A future step can add a proper "clear"
          // action that stores null.  For now 0 = "품절만 재고부족 기준".
          low_stock_threshold: t === '' ? 0 : Number(t),
        });
      }
      toast.success(deltaChanged && thresholdChanged
        ? '재고와 재고부족 기준이 저장되었습니다.'
        : deltaChanged ? '재고가 조정되었습니다.'
        : '재고부족 기준이 저장되었습니다.');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || '저장 실패'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4" data-testid="inv-adjust-modal">
      <div className="w-full sm:max-w-md bg-white dark:bg-[#141414] rounded-t-2xl sm:rounded-2xl p-5 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <p className="text-lg font-black">재고 조정</p>
          <button onClick={onClose} className="p-1"><X size={16}/></button>
        </div>
        <div className="rounded-xl border border-black/10 dark:border-white/10 p-3 mb-4 text-xs space-y-1">
          <p className="font-bold">{row.product_name}</p>
          <p className="text-gray-500">{row.variant_key === 'default' ? '기본' : row.variant_key} · SKU {row.sku || '-'}</p>
          <div className="flex justify-between mt-2 pt-2 border-t border-black/5 dark:border-white/10">
            <span className="text-gray-500">현재 총재고</span>
            <span className="font-black">{won(row.total_stock)}</span>
          </div>
        </div>
        <div className="space-y-3">
          <div>
            <p className="text-xs font-black mb-1">조정 수량 (양수=입고, 음수=출고) <span className="text-[#FF3B30]">*</span></p>
            <div className="flex gap-2">
              <button type="button" onClick={() => setDelta((d) => Number(d) - 1)} className="px-3 py-2 rounded border border-black/10 dark:border-white/10"><Minus size={12}/></button>
              <input type="number" value={delta} onChange={(e) => setDelta(e.target.value)}
                className="flex-1 px-3 py-2 text-sm rounded-lg border border-black/10 dark:border-white/10 bg-transparent text-center font-mono font-black"
                data-testid="inv-adjust-delta" />
              <button type="button" onClick={() => setDelta((d) => Number(d) + 1)} className="px-3 py-2 rounded border border-black/10 dark:border-white/10"><Plus size={12}/></button>
            </div>
            <p className="text-[10px] text-gray-500 mt-1">조정 후 예상 총재고: <span className="font-bold">{Math.max(0, Number(row.total_stock) + Number(delta || 0))}</span></p>
          </div>
          <div>
            <p className="text-xs font-black mb-1">사유</p>
            <select value={reason} onChange={(e) => setReason(e.target.value)} className="w-full px-3 py-2 text-xs rounded-lg border border-black/10 dark:border-white/10 bg-transparent" data-testid="inv-adjust-reason">
              <option>입고</option>
              <option>파손</option>
              <option>분실</option>
              <option>재고조정</option>
              <option>샘플 지급</option>
              <option>기타</option>
            </select>
          </div>
          {/* A4-1D · 입고 사유일 때만 매입처 선택 노출.  transaction 에 supplier_snapshot 저장. */}
          {isInbound && (
            <div>
              <p className="text-xs font-black mb-1">매입처</p>
              {distinctSids.length === 1 && activeLink ? (
                <div className="rounded-lg border border-emerald-300 dark:border-emerald-500/40 bg-emerald-50 dark:bg-emerald-500/10 p-3"
                     data-testid="inv-adjust-supplier-auto">
                  <div className="flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-emerald-800 dark:text-emerald-300 mb-1">
                    ✓ 상품 연결 매입처 자동 선택
                    {primaryMatchedBy === 'default' && (
                      <span className="ml-1 opacity-70">(상품 레벨 연결)</span>
                    )}
                  </div>
                  <div className="grid grid-cols-[80px_1fr] gap-y-0.5 text-[11px]">
                    <span className="text-gray-500">매입처</span>
                    <span className="font-black" data-testid="inv-adjust-supplier-name">
                      {activeLink.supplier?.name || '-'}
                    </span>
                    <span className="text-gray-500">매입처 상품명</span>
                    <span className="font-bold" data-testid="inv-adjust-supplier-productname">
                      {activeLink.supplier_product_name || activeLink.supplier?.name
                        ? (activeLink.supplier_product_name || row.product_name || '-')
                        : '-'}
                    </span>
                    <span className="text-gray-500">옵션</span>
                    <span data-testid="inv-adjust-supplier-option">
                      {activeLink.variant_option_label
                        || (row.variant_key !== 'default' ? row.variant_key : '-')}
                    </span>
                    <span className="text-gray-500">공급가</span>
                    <span className="font-black text-[#FF3B30]" data-testid="inv-adjust-supplier-price">
                      {Number(activeLink.purchase_price || 0) > 0
                        ? `${Number(activeLink.purchase_price).toLocaleString('ko-KR')}원`
                        : '-'}
                    </span>
                  </div>
                  {/* A4-1G-2A · 매입처 상품정보 (매입처 상품명 / 공급가 등) 수정 진입점.
                      매입처 상세 modal 을 위에 띄워 편집 · 재고 수량/입출고 로직 변경 없음. */}
                  <div className="mt-2 flex justify-end">
                    <button type="button"
                      onClick={() => setSupplierDetailSid(activeLink.supplier?.supplier_id || activeLink.supplier_id)}
                      className="text-[11px] font-bold text-gray-600 dark:text-gray-300 hover:text-[#FF3B30] inline-flex items-center gap-1"
                      data-testid="inv-adjust-supplier-edit">
                      매입처 상품정보 수정 →
                    </button>
                  </div>
                </div>
              ) : distinctSids.length > 1 ? (
                <>
                  <SupplierPicker value={supplierId} onChange={setSupplierId}
                                  testId="inv-adjust-supplier" />
                  <p className="text-[10px] text-amber-700 dark:text-amber-300 mt-1"
                     data-testid="inv-adjust-supplier-multi">
                    이 상품에 {distinctSids.length}개의 매입처가 연결되어 있습니다. 선택해주세요.
                  </p>
                </>
              ) : (
                <>
                  <SupplierPicker value={supplierId} onChange={setSupplierId}
                                  testId="inv-adjust-supplier" />
                  <p className="text-[10px] text-gray-500 mt-1"
                     data-testid="inv-adjust-supplier-none">
                    연결된 매입처가 없습니다. 선택 시 재고이력에 매입처가 기록됩니다.
                  </p>
                </>
              )}
            </div>
          )}
          <div>
            <p className="text-xs font-black mb-1">메모 (선택)</p>
            <input value={memo} onChange={(e) => setMemo(e.target.value)} placeholder="상세 메모"
              className="w-full px-3 py-2 text-xs rounded-lg border border-black/10 dark:border-white/10 bg-transparent"
              data-testid="inv-adjust-memo" />
          </div>
          <div>
            <p className="text-xs font-black mb-1">
              <Bell size={11} className="inline"/> 재고 부족 기준
              {initialThresholdRaw != null && (
                <span className="ml-1 text-gray-500 font-normal">(현재 {initialThresholdRaw}개 이하)</span>
              )}
              {initialThresholdRaw == null && (
                <span className="ml-1 text-gray-400 font-normal" data-testid="inv-adjust-threshold-unset">· 미설정</span>
              )}
            </p>
            <input type="number" value={threshold}
              onChange={(e) => setThreshold(e.target.value)} min={0}
              placeholder={initialThresholdRaw == null ? '미설정 (알림 기준 없음)' : ''}
              className="w-full px-3 py-2 text-xs rounded-lg border border-black/10 dark:border-white/10 bg-transparent"
              data-testid="inv-adjust-threshold" />
            <p className="text-[10px] text-gray-500 mt-1">
              {initialThresholdRaw == null
                ? '※ 미설정 시 재고부족 알림 기준을 적용하지 않습니다.'
                : '※ 향후 카카오 알림톡 재고부족/품절 알림 연결 예정 (현재 Mock 모드).'}
            </p>
          </div>
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 py-3 rounded-full border border-black/10 dark:border-white/10 text-xs font-black">취소</button>
          <button onClick={submit} disabled={busy || (!deltaChanged && !thresholdChanged)} className="flex-1 py-3 rounded-full bg-[#FF3B30] text-white text-xs font-black disabled:opacity-40" data-testid="inv-adjust-submit">
            {busy ? '저장 중…' : '저장'}
          </button>
        </div>
      </div>
      {/* A4-1G-2A · 매입처 상품정보 (매입처 상품명 / 공급가) 수정 modal · onSaved 로 activeLink refresh */}
      {supplierDetailSid && (
        <SupplierDetailModal
          sid={supplierDetailSid}
          onClose={() => setSupplierDetailSid(null)}
          onSaved={reloadPrimary}
        />
      )}
    </div>
  );
}

function TxModal({ row, onClose }) {
  const [rows, setRows] = useState([]); const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get('/seller/inventory/transactions', {
      params: { product_id: row.product_id, variant_key: row.variant_key, limit: 100 }
    }).then((r) => setRows(r.data?.items || [])).catch(() => {}).finally(() => setLoading(false));
  }, [row.product_id, row.variant_key]);

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4" onClick={onClose} data-testid="inv-tx-modal">
      <div className="w-full sm:max-w-lg bg-white dark:bg-[#141414] rounded-t-2xl sm:rounded-2xl p-5 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-lg font-black">재고 이력</p>
            <p className="text-xs text-gray-500">{row.product_name} · {row.variant_key === 'default' ? '기본' : row.variant_key}</p>
          </div>
          <button onClick={onClose} className="p-1"><X size={16}/></button>
        </div>
        {loading ? (
          <div className="py-8 text-center text-sm text-gray-400"><Loader2 className="animate-spin inline mr-1" size={14}/> 불러오는 중…</div>
        ) : rows.length === 0 ? (
          <div className="py-8 text-center text-sm text-gray-500" data-testid="inv-tx-empty">이력이 없습니다.</div>
        ) : (
          <div className="space-y-2" data-testid="inv-tx-list">
            {rows.map((r) => {
              const isIn = ['manual_in', 'return_restocked'].includes(r.type);
              const isOut = ['manual_out', 'shipment_confirmed', 'exchange_out', 'return_discarded'].includes(r.type);
              const Icon = isIn ? TrendingUp : isOut ? TrendingDown : History;
              const cls = isIn ? 'text-emerald-700' : isOut ? 'text-red-700' : 'text-gray-600';
              return (
                <div key={r.transaction_id} className="flex items-center gap-2 text-xs py-2 border-b border-black/5 dark:border-white/10">
                  <Icon size={12} className={cls} />
                  <div className="flex-1 min-w-0">
                    <p className="font-bold">{TX_TYPE_LABEL[r.type] || r.type} <span className={`font-mono ${cls}`}>{isIn ? '+' : isOut ? '-' : ''}{r.quantity}</span></p>
                    <p className="text-[10px] text-gray-500">{fmtDate(r.created_at)}{r.actor_name ? ` · ${r.actor_name}` : ''}{r.reason ? ` · ${r.reason}` : ''}</p>
                    {r.memo && <p className="text-[10px] text-gray-500 truncate">{r.memo}</p>}
                  </div>
                  <div className="text-right shrink-0 text-[10px] text-gray-500">
                    <p>{r.before_stock} → <span className="font-black text-gray-700 dark:text-gray-200">{r.after_stock}</span></p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
